import { daysUntilDeadline, isActiveBidStatus, isDeadlinePast } from "@/lib/bid-deadline"
import type { MyWorkItem } from "@/stores/my-work-store"
import type { Bid } from "@/lib/schemas"

/** "Closing soon" on My Work: the bid closes within this many days. */
export const MY_WORK_CLOSING_SOON_DAYS = 14

/** An active bid whose closing date (and time, when set) has passed. */
export function bidIsOverdue(bid: Bid | undefined, now: Date = new Date()): boolean {
  if (!bid?.deadline || bid.archived || !isActiveBidStatus(bid.status)) return false
  return isDeadlinePast(bid, now)
}

/** An active bid closing today or within MY_WORK_CLOSING_SOON_DAYS, not yet past. */
export function bidIsClosingSoon(bid: Bid | undefined, now: Date = new Date()): boolean {
  if (!bid?.deadline || bid.archived || !isActiveBidStatus(bid.status)) return false
  const days = daysUntilDeadline(bid.deadline, now)
  return days !== null && days >= 0 && days <= MY_WORK_CLOSING_SOON_DAYS && !isDeadlinePast(bid, now)
}

export interface MyWorkCounts {
  /** Sections you write: the "Assigned to me" tab. */
  assigned: number
  /** Sections you review: the "To review" tab. */
  review: number
  /** Assigned sections on an overdue bid: `?filter=overdue`. */
  overdue: number
  /** Assigned sections on a bid closing soon: `?filter=closing`. */
  closingSoon: number
}

/**
 * The counts the My Work page shows, in one place so Home's "Needs you" line
 * can't drift from them. Overdue and closing-soon count sections on the
 * Assigned tab (what each count's link opens), not bids.
 */
export function countMyWork(
  items: MyWorkItem[],
  bidsById: Map<string, Bid>,
  now: Date = new Date(),
): MyWorkCounts {
  const counts: MyWorkCounts = { assigned: 0, review: 0, overdue: 0, closingSoon: 0 }
  for (const item of items) {
    if (item.role === "reviewer") {
      counts.review += 1
      continue
    }
    if (item.role !== "writer") continue
    counts.assigned += 1
    const bid = bidsById.get(item.bidId)
    if (bidIsOverdue(bid, now)) counts.overdue += 1
    else if (bidIsClosingSoon(bid, now)) counts.closingSoon += 1
  }
  return counts
}
